import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import Header from '../components/Header'
import Footer from '../components/Footer'
import CommonHeadTags from '../components/CommonHeadTags'
import { GoogleAnalyticsHead } from '../components/GoogleAnalytics'
import ShadowCard from '../components/ShadowCard'
import withLocale from '../translations/withLocale'
import style from '../components/PrivPolicy.module.scss'

const IosPrivacy = () => {
  return (
    <>
      <Head>
        <title>iOS App Privacy Policy | Guitar-Tuner.org</title>
        <meta
          name='description'
          content='How the Guitar Tuner iOS app handles microphone audio, local data, in-app purchases, diagnostics and support requests.'
        />
        <meta httpEquiv='content-language' content='en' />
        <link rel='canonical' href='https://guitar-tuner.org/ios-privacy' />
        <link
          rel='alternate'
          hrefLang='x-default'
          href='https://guitar-tuner.org/ios-privacy'
        />
        <link
          rel='alternate'
          hrefLang='en'
          href='https://guitar-tuner.org/ios-privacy'
        />
        <CommonHeadTags
          title='iOS App Privacy Policy | Guitar-Tuner.org'
          description='How the Guitar Tuner iOS app by ChordChord OÜ handles microphone audio, local data, in-app purchases and support requests.'
          url='https://guitar-tuner.org/ios-privacy'
        />
        <GoogleAnalyticsHead enableTracking={false} />
      </Head>

      <Header />

      <main className={style.privPolicy}>
        <ShadowCard>
          <div className={style.textContainer}>
            <h1>iOS App Privacy Policy</h1>
            <p><strong>Effective date:</strong> November 3, 2026</p>

            <p>
              This Privacy Policy explains how the Guitar Tuner app for iPhone
              and iPad (the “app”) handles information. In short: the app
              listens through your microphone only to analyse sound on your
              device, it has no accounts, advertising, analytics or tracking,
              and your progress and settings stay on your device.
            </p>
            <p>
              This policy covers the iOS app only. The Guitar-Tuner.org website
              is covered by our separate{' '}
              <Link href='/privacy-policy'><a>website Privacy Policy</a></Link>.
            </p>

            <h2>Who is responsible for your data?</h2>
            <p>
              The app is provided by:
            </p>
            <p>
              <strong>ChordChord OÜ</strong><br />
              Sepapaja 6<br />
              Tallinn 15551<br />
              Estonia<br />
              Email: <a href='mailto:team@guitar-tuner.org'>team@guitar-tuner.org</a>
            </p>

            <h2>Microphone audio</h2>
            <p>
              The app asks for microphone permission so it can tune your
              instrument and recognise the notes and chords you play. The audio
              is analysed on your device only. It is never recorded, saved or
              uploaded, and it is not sent to us or to anyone else.
            </p>
            <p>
              You can refuse microphone access, or revoke it at any time in iOS
              Settings. Features that do not listen through the microphone, such
              as reference sounds, remain available.
            </p>

            <h2>Data stored on your device</h2>
            <p>
              Your settings, saved chords and learning progress are kept in the
              app’s local storage on your device. They are not sent to us. If
              you delete the app, this data is removed from the device, subject
              to the backup and restore settings you control through your device
              and Apple.
            </p>

            <h2>No accounts, advertising, analytics or tracking</h2>
            <p>
              The app does not require or offer an account. It contains no
              advertising and no third-party analytics or tracking SDKs, and it
              does not track you across other companies’ apps or websites. We
              do not sell or share personal data.
            </p>

            <h2>In-app purchases</h2>
            <p>
              The app is free to download. Optional Premium subscriptions and a
              lifetime purchase unlock additional features. All purchases are
              processed entirely by Apple through your Apple Account. We do not
              receive your payment card details or other personal data about
              you from your purchase.
            </p>
            <p>
              To know whether Premium is unlocked, the app checks your purchase
              status on your device using Apple’s StoreKit framework. Apple’s
              handling of purchase information is governed by Apple’s own terms
              and privacy policy.
            </p>
            <p>
              You can manage or cancel a subscription at any time in your Apple
              Account settings (on iPhone or iPad: Settings &gt; your name &gt;
              Subscriptions). Deleting the app does not cancel a subscription.
            </p>

            <h2>Diagnostics from Apple</h2>
            <p>
              If you choose to share analytics with app developers in your
              device settings, Apple may provide us with crash reports and
              aggregated diagnostic and usage information about the app. Apple
              provides this under its own terms and privacy policy, and we use
              it only to find and fix problems. You can turn this sharing off at
              any time in iOS Settings under Privacy &amp; Security &gt;
              Analytics &amp; Improvements.
            </p>

            <h2>If you contact us</h2>
            <p>
              If you email us at{' '}
              <a href='mailto:team@guitar-tuner.org'>team@guitar-tuner.org</a>,
              we receive your email address, the contents of your message and
              any information you choose to include. We use it only to respond
              to you and keep an appropriate record of the request, and we keep
              it only for as long as needed for that purpose.
            </p>

            <h2>Links to websites</h2>
            <p>
              The app may link to Guitar-Tuner.org or other websites. Visiting a
              website is separate from using the app and is covered by that
              website’s own privacy policy. For Guitar-Tuner.org, see our{' '}
              <Link href='/privacy-policy'><a>website Privacy Policy</a></Link>.
            </p>

            <h2>Your data protection rights</h2>
            <p>
              Depending on your location and applicable law, you may have the
              right to request access to, correction of, deletion of, or a copy
              of your personal data; to restrict or object to processing; and
              to withdraw consent. Because the app keeps your data on your
              device, the main personal data we may hold about you is what you
              send us when you contact us. You may also have the right to lodge
              a complaint with a data protection authority.
            </p>
            <p>
              To exercise your rights, email{' '}
              <a href='mailto:team@guitar-tuner.org'>team@guitar-tuner.org</a>.
              In Estonia, the supervisory authority is the{' '}
              <a href='https://www.aki.ee/en'>Estonian Data Protection Inspectorate</a>.
            </p>

            <h2>Children’s privacy</h2>
            <p>
              The app is a general-audience music tool and is not directed
              specifically to children. It does not collect personal information
              from anyone who uses it. If you believe a child has sent us
              personal information, contact us so we can review and delete it
              where appropriate.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              We may update this Privacy Policy when the app, the services it
              uses, or legal requirements change. We will publish the revised
              version on this page and update the effective date at the top.
            </p>

            <h2>Contact</h2>
            <p>
              Questions about this policy or your privacy can be sent to{' '}
              <a href='mailto:team@guitar-tuner.org'>team@guitar-tuner.org</a>{' '}
              or by post to ChordChord OÜ, Sepapaja 6, Tallinn 15551, Estonia.
            </p>
          </div>
        </ShadowCard>
      </main>

      <Footer />
    </>
  )
}

export default withLocale(IosPrivacy)
